/* Klecksy cart: gift wrap + printed letter sync, set and same-print suggestions, letter designer.
   Loaded as a module by snippets/klecksy-cart-extras.liquid and blocks/klecksy-set-button.liquid.
   Talks to Horizon through the standard cart event, so the drawer and cart page re-render themselves. */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

if (!window.__klecksyCart) {
  window.__klecksyCart = true;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const MAX_MSG = 400;
  let busy = false;

  const sectionIds = () =>
    [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
  const giftBox = () => document.querySelector('[data-kx-gift]');

  /* ---------- Cart requests that Horizon re-renders from ---------- */
  async function cartCall(path, { json, form, action = 'update', lines = [] } = {}) {
    const ids = sectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));
    try {
      let init;
      if (form) {
        if (ids.length) form.append('sections', ids.join(','));
        init = { method: 'POST', body: form, headers: { Accept: 'application/json' } };
      } else {
        const body = { ...json };
        if (ids.length) body.sections = ids.join(',');
        init = { method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
      }
      const res = await fetch(root + path, init);
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not update your cart. Please try again.');
      const cart = 'item_count' in data ? data : await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { sections: data.sections, items: cart.items, itemCount: cart.item_count, source: 'klecksy-cart', didError: false },
      });
      return cart;
    } catch (err) {
      deferred.reject(err);
      throw err;
    }
  }

  const update = (updates) =>
    cartCall('cart/update.js', {
      json: { updates },
      lines: Object.entries(updates).map(([id, quantity]) => ({ id: String(id), quantity: Number(quantity) })),
      action: Object.values(updates).every((q) => Number(q) === 0) ? 'remove' : 'update',
    });
  const changeLine = (key, quantity) =>
    cartCall('cart/change.js', { json: { id: key, quantity }, lines: [{ id: key, quantity }], action: quantity > 0 ? 'update' : 'remove' });
  const addItems = (items) =>
    cartCall('cart/add.js', { json: { items }, action: 'add', lines: items.map((i) => ({ merchandiseId: `gid://shopify/ProductVariant/${i.id}`, quantity: i.quantity })) });

  function showError(msg) {
    const el = giftBox()?.querySelector('.kx-gift__err');
    if (el) { el.textContent = msg; el.hidden = !msg; }
  }

  async function run(fn) {
    if (busy) return;
    busy = true;
    const box = giftBox();
    box?.setAttribute('aria-busy', 'true');
    try { await fn(); showError(''); }
    catch (err) { showError(err.message); }
    finally { busy = false; giftBox()?.removeAttribute('aria-busy'); setTimeout(sync, 350); }
  }

  /* ---------- Keep add-ons consistent with the cart ---------- */
  async function sync() {
    if (busy) return;
    const orphans = document.querySelector('[data-kx-orphans]');
    if (orphans) {
      const u = {};
      orphans.dataset.ids.split(',').filter(Boolean).forEach((id) => (u[id] = 0));
      if (Object.keys(u).length) return run(() => update(u));
    }
    const box = giftBox();
    if (!box) return;
    const d = box.dataset;
    const pieces = Number(d.pieces);
    if (Number(d.wrapQ) > 0 && Number(d.wrapQ) !== pieces) return run(() => update({ [d.wrapId]: pieces }));
    if (Number(d.letterQ) > 1 && d.letterKey) return run(() => changeLine(d.letterKey, 1));
    if (d.letterKey && d.letterVariant && d.letterVariant !== d.letterWant) {
      return run(async () => {
        const props = {};
        if (d.letterTo) props.To = d.letterTo;
        if (d.letterMsg) props.Message = d.letterMsg;
        if (d.letterFrom) props.From = d.letterFrom;
        if (d.letterFile) props['_Letter file'] = d.letterFile;
        await changeLine(d.letterKey, 0);
        await addItems([{ id: Number(d.letterWant), quantity: 1, properties: props }]);
      });
    }
  }

  /* ---------- Cart count shows pieces only (gift wrap and letter aren't pieces) ---------- */
  function piecesNow() {
    const el = document.querySelector('[data-kx-pieces]');
    return el ? Number(el.dataset.kxPieces) : null;
  }
  function fixCounts() {
    const n = piecesNow();
    if (n === null) return;
    document.querySelectorAll('.cart-bubble__text-count, .theme-drawer__badge').forEach((el) => {
      if (el.textContent.trim() !== String(n)) el.textContent = String(n);
    });
  }
  const countObserver = new MutationObserver(() => fixCounts());
  function watchCounts() {
    countObserver.disconnect();
    document.querySelectorAll('.cart-bubble__text-count, .theme-drawer__badge').forEach((el) =>
      countObserver.observe(el, { childList: true, characterData: true, subtree: true })
    );
    fixCounts();
  }

  document.addEventListener(StandardEvents.cartLinesUpdate, (e) => {
    if (e.promise) e.promise.then(() => setTimeout(() => { sync(); watchCounts(); }, 450)).catch(() => {});
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { sync(); watchCounts(); });
  else { sync(); watchCounts(); }

  /* ---------- Clicks: wrap switch, set add, letter ---------- */
  document.addEventListener('click', async (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;

    const wrap = t.closest('[data-kx-wrap]');
    if (wrap) {
      e.preventDefault();
      const d = giftBox().dataset;
      const on = wrap.getAttribute('aria-checked') !== 'true';
      wrap.setAttribute('aria-checked', String(on));
      return run(() => update({ [d.wrapId]: on ? Number(d.pieces) : 0 }));
    }

    const setAdd = t.closest('[data-kx-set-add]');
    if (setAdd) {
      e.preventDefault();
      const card = setAdd.closest('[data-kx-setcard]');
      const id = Number(card.querySelector('[data-kx-set-size]:checked')?.value);
      if (!id) { alertInline(card, 'Pick a size first.'); return; }
      setAdd.disabled = true;
      setAdd.textContent = 'Adding…';
      try { await addItems([{ id, quantity: 1 }]); }
      catch (err) { setAdd.disabled = false; setAdd.textContent = 'Add'; alertInline(card, err.message); }
      return;
    }

    const lookAdd = t.closest('[data-kx-look-add]');
    if (lookAdd) {
      e.preventDefault();
      return addSuggestion(lookAdd);
    }

    const setBtn = t.closest('[data-kx-set-product]');
    if (setBtn) {
      e.preventDefault();
      return addSetFromProduct(setBtn);
    }

    const letter = t.closest('[data-kx-letter]');
    if (letter) {
      e.preventDefault();
      const mode = letter.dataset.kxLetter;
      const d = giftBox().dataset;
      if (mode === 'remove') return run(() => changeLine(d.letterKey, 0));
      openLetter(mode === 'edit' ? { to: d.letterTo || '', message: d.letterMsg || '', from: d.letterFrom || '' } : null, letter);
    }
  });

  document.addEventListener('change', (e) => {
    const input = e.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.closest('variant-picker')) return;
    const size = input.value?.trim();
    document.querySelectorAll('[data-kx-set]').forEach((set) => {
      const match = [...set.querySelectorAll('[data-kx-set-mate]')].find((r) => r.nextElementSibling?.textContent.trim() === size && !r.disabled);
      if (match) match.checked = true;
    });
    // Same-print row: follow the size picked above when that size exists for the suggested piece
    document.querySelectorAll('[data-kx-family] [data-kx-look-size]').forEach((sel) => {
      const opt = [...sel.options].find((o) => o.textContent.trim() === size && !o.disabled);
      if (opt) sel.value = opt.value;
    });
  });

  /* ---------- Suggestions (cart "Complete the look" + product "Same print") ---------- */
  function openDrawer() {
    const drawer = document.querySelector('#cart-drawer');
    if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) setTimeout(() => drawer.open(), 250);
  }
  async function addSuggestion(btn) {
    const item = btn.closest('[data-kx-look-item]') || btn.parentElement;
    const sel = item.querySelector('[data-kx-look-size]');
    const id = Number(sel ? sel.value : btn.dataset.variant);
    if (!id) { alertInline(item, 'Pick a size first.'); return; }
    const old = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    item.setAttribute('aria-busy', 'true');
    try {
      await addItems([{ id, quantity: 1 }]);
      btn.textContent = 'Added';
      if ('openDrawer' in btn.dataset) openDrawer();
      setTimeout(() => { if (btn.isConnected) { btn.textContent = old; btn.disabled = false; } }, 1800);
    } catch (err) {
      btn.textContent = old;
      btn.disabled = false;
      alertInline(item, err.message);
    } finally {
      item.removeAttribute('aria-busy');
    }
  }

  function alertInline(container, msg) {
    let p = container.querySelector('.kx-inline-err');
    if (!p) { p = document.createElement('p'); p.className = 'kx-inline-err kx-gift__err'; p.setAttribute('role', 'alert'); container.append(p); }
    p.textContent = msg;
  }

  /* ---------- Product page: add tee + matching piece ---------- */
  async function addSetFromProduct(btn) {
    const wrap = btn.closest('[data-kx-set]');
    // This piece uses the size picked in the product form above; fall back to the block's default.
    const picked = document.querySelector(`product-form-component[data-product-id="${wrap.dataset.product}"] [name="id"]`);
    const mainVar = picked?.value || wrap.dataset.main;
    const mateVar = wrap.querySelector('[data-kx-set-mate]:checked')?.value;
    if (!mainVar || !mateVar) { alertInline(wrap, 'Pick a size for both pieces.'); return; }
    const label = btn.querySelector('[data-kx-set-label]') || btn;
    const old = label.textContent;
    btn.disabled = true;
    label.textContent = 'Adding the set…';
    try {
      await addItems([{ id: Number(mainVar), quantity: 1 }, { id: Number(mateVar), quantity: 1 }]);
      label.textContent = 'Set added';
      openDrawer();
      setTimeout(() => { label.textContent = old; btn.disabled = false; }, 1800);
    } catch (err) {
      label.textContent = old; btn.disabled = false;
      alertInline(wrap, err.message);
    }
  }

  /* ---------- Letter designer ---------- */
  let dlg = null;

  function buildDialog(host) {
    dlg = document.createElement('dialog');
    dlg.className = 'kx-letter';
    dlg.setAttribute('aria-labelledby', 'kx-letter-title');
    dlg.innerHTML = `
      <div class="kx-letter__inner">
        <div class="kx-letter__head">
          <div>
            <h2 class="kx-letter__title" id="kx-letter-title">Write a printed letter</h2>
            <p class="kx-letter__sub">We print it on an A5 Klecksy card and pack it with the order.</p>
          </div>
          <button type="button" class="kx-letter__close" data-kx-letter-close aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>
          </button>
        </div>
        <form novalidate>
          <label>To<input name="to" maxlength="40" autocomplete="off" placeholder="Riya"></label>
          <label>Message<textarea name="message" maxlength="${MAX_MSG}" required placeholder="Happy birthday! Saw this print and thought of you."></textarea></label>
          <span class="kx-letter__count" aria-live="polite">0 / ${MAX_MSG}</span>
          <label>From<input name="from" maxlength="40" autocomplete="off" placeholder="Aarav"></label>
          <p class="kx-letter__note">We print your words exactly as typed, so check the spelling. Any language and emoji work. Prices are never included in gift parcels.</p>
          <p class="kx-letter__err" role="alert" hidden></p>
          <button type="submit" class="kx-letter__submit">Add letter to order</button>
        </form>
        <figure class="kx-letter__preview">
          <canvas width="874" height="1240" aria-label="Preview of your printed letter"></canvas>
          <figcaption>Live preview · A5</figcaption>
        </figure>
      </div>`;
    host.append(dlg);

    const form = dlg.querySelector('form');
    const canvas = dlg.querySelector('canvas');
    const count = dlg.querySelector('.kx-letter__count');
    let raf = 0;
    dlg.paint = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        count.textContent = `${form.message.value.length} / ${MAX_MSG}`;
        drawLetter(canvas, readForm(form), 0.5);
      });
    };
    form.addEventListener('input', dlg.paint);
    dlg.querySelector('[data-kx-letter-close]').addEventListener('click', () => dlg.close());
    dlg.addEventListener('click', (e) => {
      if (e.target !== dlg) return;
      const r = dlg.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dlg.close();
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const data = readForm(form);
      const err = dlg.querySelector('.kx-letter__err');
      if (!data.message) { err.textContent = 'Write a message first.'; err.hidden = false; form.message.focus(); return; }
      err.hidden = true;
      const btn = form.querySelector('.kx-letter__submit');
      btn.disabled = true;
      btn.textContent = 'Creating your letter…';
      try {
        await fontsReady();
        const full = document.createElement('canvas');
        full.width = 1748; full.height = 2480;
        drawLetter(full, data, 1);
        const blob = await new Promise((res, rej) => full.toBlob((b) => (b ? res(b) : rej(new Error('Could not create the letter image.'))), 'image/png'));
        const d = giftBox().dataset;
        busy = true;
        try {
          if (d.letterKey) await changeLine(d.letterKey, 0);
          const fd = new FormData();
          fd.append('id', d.letterWant);
          fd.append('quantity', '1');
          if (data.to) fd.append('properties[To]', data.to);
          fd.append('properties[Message]', data.message);
          if (data.from) fd.append('properties[From]', data.from);
          fd.append('properties[_Letter file]', blob, 'klecksy-letter.png');
          await cartCall('cart/add.js', { form: fd, action: 'add', lines: [{ merchandiseId: `gid://shopify/ProductVariant/${d.letterWant}`, quantity: 1 }] });
        } finally {
          busy = false;
        }
        dlg.close();
        setTimeout(sync, 450);
      } catch (ex) {
        err.textContent = ex.message || 'Could not add the letter. Please try again.';
        err.hidden = false;
      } finally {
        btn.disabled = false;
        btn.textContent = 'Add letter to order';
      }
    });
  }

  function readForm(form) {
    return {
      to: form.to.value.trim(),
      message: form.message.value.replace(/\r/g, '').trim().slice(0, MAX_MSG),
      from: form.from.value.trim(),
    };
  }

  async function openLetter(prefill, trigger) {
    // Nest the dialog inside the drawer's own <dialog> so it stacks above it (a body-level dialog can't open over a modal drawer).
    const host = trigger.closest('dialog') || document.body;
    if (!dlg || dlg.parentElement !== host) { dlg?.remove(); buildDialog(host); }
    const form = dlg.querySelector('form');
    form.to.value = prefill?.to || '';
    form.message.value = prefill?.message || '';
    form.from.value = prefill?.from || '';
    dlg.querySelector('.kx-letter__err').hidden = true;
    dlg.showModal();
    dlg.paint();
    fontsReady().then(dlg.paint);
    setTimeout(() => form.to.focus(), 60);
  }

  /* ---------- Drawing the A5 letter (1748 x 2480 px = A5 at 300 dpi) ---------- */
  const cssVar = (name, fallback) => getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
  const FONTS = () => ({
    display: cssVar('--font-heading--family', 'system-ui, sans-serif'),
    accent: cssVar('--font-accent--family', 'Georgia, serif'),
    body: cssVar('--font-body--family', 'system-ui, sans-serif'),
    label: cssVar('--font-subheading--family', 'ui-monospace, monospace'),
  });
  let fontsPromise = null;
  function fontsReady() {
    if (fontsPromise) return fontsPromise;
    const f = FONTS();
    fontsPromise = Promise.all([
      document.fonts.load(`800 100px ${f.display}`),
      document.fonts.load(`italic 400 100px ${f.accent}`),
      document.fonts.load(`400 100px ${f.body}`),
      document.fonts.load(`500 100px ${f.label}`),
    ]).catch(() => {}).then(() => document.fonts.ready);
    return fontsPromise;
  }

  const INK = '#16130E';
  const PALETTE = ['#FF5A36', '#2D4BFF', '#C9B6FF', '#FFC93C', '#1F4D35', '#FF8FB1'];

  function seeded(seed) {
    let s = seed >>> 0;
    return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
  }

  function blob(ctx, x, y, r, rnd) {
    ctx.beginPath();
    const n = 7;
    for (let i = 0; i <= n; i++) {
      const a = (i / n) * Math.PI * 2;
      const rr = r * (0.82 + rnd() * 0.36);
      const px = x + Math.cos(a) * rr, py = y + Math.sin(a) * rr;
      if (i === 0) ctx.moveTo(px, py); else ctx.quadraticCurveTo(x + Math.cos(a - Math.PI / n) * rr * 1.08, y + Math.sin(a - Math.PI / n) * rr * 1.08, px, py);
    }
    ctx.closePath();
    ctx.fill();
  }

  function band(ctx, x0, y0, w, h, rnd) {
    ctx.save();
    ctx.beginPath(); ctx.rect(x0, y0, w, h); ctx.clip();
    for (let i = 0; i < 46; i++) {
      const x = x0 + rnd() * w, y = y0 + rnd() * h, r = 14 + rnd() * 34;
      ctx.fillStyle = PALETTE[Math.floor(rnd() * PALETTE.length)];
      const kind = rnd();
      if (kind < 0.55) blob(ctx, x, y, r, rnd);
      else if (kind < 0.8) {
        ctx.strokeStyle = ctx.fillStyle; ctx.lineWidth = 10; ctx.lineCap = 'round';
        ctx.beginPath(); ctx.moveTo(x - r * 1.4, y);
        ctx.bezierCurveTo(x - r * 0.6, y - r, x + r * 0.2, y + r, x + r * 1.4, y - r * 0.2); ctx.stroke();
      } else {
        ctx.beginPath(); ctx.arc(x, y, r * 0.45, 0, Math.PI * 2); ctx.fill();
      }
    }
    ctx.restore();
  }

  function wrapLines(ctx, text, maxW) {
    const out = [];
    text.split('\n').forEach((para) => {
      if (!para.trim()) { out.push(''); return; }
      const words = para.split(/(\s+)/);
      let line = '';
      words.forEach((w) => {
        const test = line + w;
        if (ctx.measureText(test).width > maxW && line.trim()) {
          out.push(line.trimEnd());
          line = w.trimStart();
          // very long single words (or scripts without spaces): hard-break
          while (ctx.measureText(line).width > maxW) {
            let cut = line.length - 1;
            while (cut > 1 && ctx.measureText(line.slice(0, cut)).width > maxW) cut--;
            out.push(line.slice(0, cut));
            line = line.slice(cut);
          }
        } else line = test;
      });
      out.push(line.trimEnd());
    });
    return out;
  }

  function drawLetter(canvas, data, scale) {
    const ctx = canvas.getContext('2d');
    const W = 1748, H = 2480;
    const f = FONTS();
    ctx.setTransform(canvas.width / W, 0, 0, canvas.height / H, 0, 0);
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, W, H);
    const rnd = seeded(20261006);

    // Pattern bands (top and bottom) with pinked edges
    band(ctx, 0, 0, W, 300, rnd);
    band(ctx, 0, H - 150, W, 150, rnd);
    ctx.fillStyle = '#FFFFFF';
    const zig = (y, dir) => {
      ctx.beginPath(); ctx.moveTo(0, y);
      for (let x = 0; x <= W; x += 44) ctx.lineTo(x + 22, y + dir * 22), ctx.lineTo(x + 44, y);
      ctx.lineTo(W, dir > 0 ? y + 200 : y - 200); ctx.lineTo(0, dir > 0 ? y + 200 : y - 200); ctx.closePath(); ctx.fill();
    };
    zig(300, 1);
    zig(H - 150, -1);

    const M = 150, maxW = W - M * 2;
    ctx.fillStyle = INK;
    ctx.textBaseline = 'alphabetic';

    // Wordmark
    ctx.font = `800 120px ${f.display}`;
    ctx.fillText('Klecksy', M, 520);
    const wm = ctx.measureText('Klecksy').width;
    ctx.fillStyle = '#FF5A36';
    blob(ctx, M + wm + 34, 488, 20, seeded(7));

    // To
    let y = 720;
    ctx.fillStyle = INK;
    ctx.font = `500 38px ${f.label}`;
    if ('letterSpacing' in ctx) ctx.letterSpacing = '6px';
    ctx.fillText('A LITTLE NOTE FOR', M, y);
    if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';
    y += 130;
    ctx.font = `italic 400 120px ${f.accent}`;
    const toText = data.to || 'someone special';
    ctx.fillText(fitText(ctx, toText, maxW), M, y);

    // Message: shrink to fit between y+120 and the sign-off
    const top = y + 140, bottom = H - 520;
    const msg = data.message || 'Your message will appear here.';
    let size = 84, lines = [];
    for (; size >= 40; size -= 2) {
      ctx.font = `400 ${size}px ${f.body}`;
      lines = wrapLines(ctx, msg, maxW);
      if (lines.length * size * 1.45 <= bottom - top) break;
    }
    ctx.globalAlpha = data.message ? 1 : 0.35;
    let ly = top + size;
    lines.forEach((ln) => { ctx.fillText(ln, M, ly); ly += size * 1.45; });
    ctx.globalAlpha = 1;

    // From
    const fy = H - 380;
    ctx.font = `500 38px ${f.label}`;
    if ('letterSpacing' in ctx) ctx.letterSpacing = '6px';
    ctx.fillText('WITH LOVE,', M, fy - 110);
    if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';
    ctx.font = `italic 400 104px ${f.accent}`;
    ctx.fillText(fitText(ctx, data.from || 'you', maxW), M, fy);
  }

  function fitText(ctx, text, maxW) {
    if (ctx.measureText(text).width <= maxW) return text;
    let t = text;
    while (t.length > 1 && ctx.measureText(t + '…').width > maxW) t = t.slice(0, -1);
    return t + '…';
  }
}
